import { useState, useEffect, useRef, type ReactNode } from 'react';

interface ScreenTransitionProps {
  transitionKey: string;
  children: ReactNode;
}

export default function ScreenTransition({ transitionKey, children }: ScreenTransitionProps) {
  const [phase, setPhase] = useState<'visible' | 'exiting' | 'entering'>('visible');
  const prevKey = useRef(transitionKey);
  const isFirstRender = useRef(true);
  const isDashboardEntry = useRef(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (transitionKey !== prevKey.current) {
      isDashboardEntry.current = transitionKey === 'dashboard';
      prevKey.current = transitionKey;
      setPhase('exiting');

      const enterDelay = isDashboardEntry.current ? 100 : 0;

      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setPhase('entering');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setPhase('visible');
              });
            });
          }, enterDelay);
        });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [transitionKey]);

  const dashboardEnter = isDashboardEntry.current && (phase === 'entering' || phase === 'visible');

  const style: React.CSSProperties = {
    willChange: phase === 'visible' ? 'auto' : 'opacity, transform',
    ...(phase === 'exiting' && {
      opacity: 0,
      transition: 'none',
    }),
    ...(phase === 'entering' && dashboardEnter && {
      opacity: 0,
      transform: 'translateX(100%)',
      transition: 'none',
    }),
    ...(phase === 'entering' && !dashboardEnter && {
      opacity: 0,
      transition: 'none',
    }),
    ...(phase === 'visible' && dashboardEnter && {
      opacity: 1,
      transform: 'translateX(0)',
      transition: 'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
    }),
    ...(phase === 'visible' && !dashboardEnter && {
      opacity: 1,
      transition: 'opacity 200ms ease-in-out',
    }),
  };

  return (
    <div style={{ ...style, overflow: phase !== 'visible' ? 'hidden' : undefined }} className="h-full">
      {children}
    </div>
  );
}
